import { Device } from '../models/device';

// ---------------------------------------------------------------------------
// STUBBED DATA + API
//
// This module stands in for the GraphQL `devices` query / `pingDevice` mutation
// that will eventually live here (defined inline with `gql` in Devices.tsx, the
// same way Orders.tsx does it — see CLAUDE.md "Two parallel state systems").
//
// Everything below is mock data and faked latency so the screen is fully
// interactive without a backend. To wire up the real API:
//   1. Replace `fetchDevices()` with a `useQuery(GET_DEVICES_QUERY)` call.
//   2. Replace `pingDevice()` with a `useMutation(PING_DEVICE_MUTATION)` call.
//   3. Delete this file.
// The component contracts (Device[] in, Promise<Device> out) are kept identical
// to the expected query/mutation results to make that swap mechanical.
// ---------------------------------------------------------------------------

// Placeholder still frame served from /public. Real frames come from the
// camera's `imageUrl` once the video pipeline is connected.
const SAMPLE_FRAME = '/grill-cam.jpg';

const STUB_DEVICES: Device[] = [
  {
    id: '1042',
    serial: '40028410551027746301',
    status: 'ONLINE',
    lastPingLabel: 'pinged just now',
    locationName: "Web's Restaurant",
    cameras: [
      { id: 'c-1042-1', name: 'Grill Cam', status: 'ONLINE', imageUrl: SAMPLE_FRAME },
      { id: 'c-1042-2', name: 'Assembly Cam', status: 'ONLINE', imageUrl: SAMPLE_FRAME },
      { id: 'c-1042-3', name: 'Expo Cam', status: 'PENDING', imageUrl: null },
    ],
  },
  {
    id: '2317',
    serial: '40028410559920018844',
    status: 'OFFLINE',
    lastPingLabel: 'last reachable 2h ago',
    locationName: 'Downtown Kitchen',
    cameras: [
      { id: 'c-2317-1', name: 'Grill Cam', status: 'ONLINE', imageUrl: SAMPLE_FRAME },
      { id: 'c-2317-2', name: 'Fryer Cam', status: 'OFFLINE', imageUrl: null },
    ],
  },
  {
    id: '3589',
    serial: '40028410557731266590',
    status: 'PENDING',
    lastPingLabel: 'not yet pinged',
    locationName: 'Airport Terminal C',
    cameras: [
      { id: 'c-3589-1', name: 'Grill Cam', status: 'OFFLINE', imageUrl: null },
      { id: 'c-3589-2', name: 'Drive-Thru Cam', status: 'PENDING', imageUrl: null },
    ],
  },
  {
    id: '4760',
    serial: '40028410552248903117',
    status: 'ONLINE',
    lastPingLabel: 'pinged 5m ago',
    locationName: 'Lincoln Park Express',
    cameras: [],
  },
  // Unpaired devices: provisioned hardware not yet associated with a location.
  // No cameras section — only the online/offline state in the header.
  {
    id: '4821',
    serial: '83920147756103948265',
    status: 'ONLINE',
    lastPingLabel: 'pinged 1m ago',
    locationName: null,
    cameras: [],
  },
  {
    id: '7193',
    serial: '11574802369014457820',
    status: 'OFFLINE',
    lastPingLabel: 'last reachable 6h ago',
    locationName: null,
    cameras: [],
  },
];

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Stubbed `devices` query. Returns a defensive copy after a short faked delay.
export async function fetchDevices(): Promise<Device[]> {
  await delay(650);
  return STUB_DEVICES.map((d) => ({ ...d, cameras: d.cameras.map((c) => ({ ...c })) }));
}

// Stubbed `pingDevice` mutation. Resolves to the device's new state: a pending
// device that answers comes back ONLINE; an offline one stays OFFLINE.
export async function pingDevice(device: Device): Promise<Device> {
  await delay(1300);
  const status: Device['status'] = device.status === 'PENDING' ? 'ONLINE' : device.status;
  return {
    ...device,
    status,
    lastPingLabel: status === 'OFFLINE' ? 'last reachable 2h ago' : 'pinged just now',
  };
}
